import * as React from 'react';
import Typography from '@mui/material/Typography';
import { motion, useReducedMotion } from 'framer-motion';

const MotionTypography = motion(Typography);

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.055, delayChildren: 0.05 } },
};

const word = {
  hidden: { opacity: 0, y: '0.4em', filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: '0em',
    filter: 'blur(0px)',
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * A heading whose words fade + rise + sharpen one-by-one as it scrolls into view.
 *
 * @param {Array<{text: string, color?: string}>} segments - text pieces to render,
 *        each optionally with its own color. Each segment is split into words.
 */
export default function RevealHeading({
  segments,
  variant = 'h2',
  component = 'h2',
  amount = 0.5,
  sx,
}) {
  const reduce = useReducedMotion();

  if (reduce) {
    return (
      <Typography variant={variant} component={component} sx={sx}>
        {segments.map((seg, si) => (
          <span key={si} style={{ color: seg.color }}>
            {seg.text}
          </span>
        ))}
      </Typography>
    );
  }

  let key = 0;
  return (
    <MotionTypography
      variant={variant}
      component={component}
      sx={sx}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={container}
    >
      {segments.map((seg, si) =>
        seg.text.split(' ').map((w, wi) => (
          <motion.span
            key={`${si}-${wi}-${key++}`}
            variants={word}
            style={{
              display: 'inline-block',
              whiteSpace: 'pre',
              color: seg.color,
              willChange: 'transform, opacity, filter',
            }}
          >
            {w}
            {' '}
          </motion.span>
        )),
      )}
    </MotionTypography>
  );
}
